/** Card do calendário: toolbar (< > Hoje | título | Mês/Semana/Dia/Lista) + views. */
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { capitalize, toISODay, type AgendaEventItem } from './agenda-format';
import { FullEventRow, MonthEventRow, eventKey, type AgendaRowMode } from './event-rows';

export type AgendaView = 'month' | 'week' | 'day' | 'list';

const WEEKDAY_HEADERS = ['DOM.', 'SEG.', 'TER.', 'QUA.', 'QUI.', 'SEX.', 'SÁB.'];

const VIEW_OPTIONS: { value: AgendaView; label: string }[] = [
  { value: 'month', label: 'Mês' },
  { value: 'week', label: 'Semana' },
  { value: 'day', label: 'Dia' },
  { value: 'list', label: 'Lista' },
];

const MAX_PER_MONTH_CELL = 3;

function title(view: AgendaView, anchor: Date, days: Date[]): string {
  if (view === 'month' || view === 'list') {
    return capitalize(format(anchor, "MMMM 'de' yyyy", { locale: ptBR }));
  }
  if (view === 'week') {
    const first = days[0] ?? anchor;
    const last = days[days.length - 1] ?? anchor;
    if (first.getMonth() === last.getMonth()) {
      return `${first.getDate()} – ${format(last, "d 'de' MMMM 'de' yyyy", { locale: ptBR })}`;
    }
    return `${format(first, "d 'de' MMM", { locale: ptBR })} – ${format(last, "d 'de' MMM 'de' yyyy", { locale: ptBR })}`;
  }
  return capitalize(format(anchor, "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR }));
}

function chunkWeeks(days: Date[]): Date[][] {
  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return weeks;
}

function WeekdayHeaderRow() {
  return (
    <div className="grid grid-cols-7 border-b bg-muted/50">
      {WEEKDAY_HEADERS.map((label) => (
        <div
          key={label}
          className="border-r py-1.5 text-center text-[13px] font-medium text-muted-foreground last:border-r-0"
        >
          {label}
        </div>
      ))}
    </div>
  );
}

export function AgendaCalendar({
  view,
  anchor,
  days,
  byDay,
  loading,
  onPrev,
  onNext,
  onToday,
  onViewChange,
  onEventClick,
  onMoreClick,
  mode = 'general',
}: {
  view: AgendaView;
  anchor: Date;
  days: Date[];
  byDay: Map<string, AgendaEventItem[]>;
  loading: boolean;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onViewChange: (view: AgendaView) => void;
  onEventClick: (item: AgendaEventItem) => void;
  onMoreClick: (dayISO: string) => void;
  /** spec 039: a aba de placas desenha o compromisso como `HH:MM PLACA` + situação */
  mode?: AgendaRowMode;
}) {
  const todayISO = toISODay(new Date());

  return (
    <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-divider px-3 py-2">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon-sm" onClick={onPrev} aria-label="Período anterior">
            <ChevronLeft />
          </Button>
          <Button variant="outline" size="icon-sm" onClick={onNext} aria-label="Próximo período">
            <ChevronRight />
          </Button>
          <Button variant="outline" size="sm" onClick={onToday}>
            Hoje
          </Button>
        </div>
        <div className="text-base font-semibold">{title(view, anchor, days)}</div>
        <div className="flex overflow-hidden rounded-md border shadow-xs" role="tablist">
          {VIEW_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="tab"
              aria-selected={view === opt.value}
              onClick={() => onViewChange(opt.value)}
              className={cn(
                'h-8 cursor-pointer border-r px-3 text-sm transition-colors last:border-r-0',
                view === opt.value
                  ? 'bg-primary/10 font-medium text-primary-pressed'
                  : 'bg-card text-muted-foreground hover:bg-muted'
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Corpo ───────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="space-y-2 p-4" aria-busy>
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-[480px] w-full" />
        </div>
      ) : view === 'month' ? (
        <MonthView
          anchor={anchor}
          weeks={chunkWeeks(days)}
          byDay={byDay}
          todayISO={todayISO}
          onEventClick={onEventClick}
          onMoreClick={onMoreClick}
          mode={mode}
        />
      ) : view === 'week' ? (
        <WeekView days={days} byDay={byDay} todayISO={todayISO} onEventClick={onEventClick} mode={mode} />
      ) : view === 'day' ? (
        <DayView dayISO={toISODay(anchor)} byDay={byDay} onEventClick={onEventClick} mode={mode} />
      ) : (
        <ListView days={days} byDay={byDay} onEventClick={onEventClick} mode={mode} />
      )}
    </div>
  );
}

// ── Mês ─────────────────────────────────────────────────────────────────────

function MonthView({
  anchor,
  weeks,
  byDay,
  todayISO,
  onEventClick,
  onMoreClick,
  mode,
}: {
  anchor: Date;
  weeks: Date[][];
  byDay: Map<string, AgendaEventItem[]>;
  todayISO: string;
  onEventClick: (item: AgendaEventItem) => void;
  onMoreClick: (dayISO: string) => void;
  mode: AgendaRowMode;
}) {
  const month = anchor.getMonth();
  return (
    <div>
      <WeekdayHeaderRow />
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7">
          {week.map((day) => {
            const iso = toISODay(day);
            const events = byDay.get(iso) ?? [];
            const shown = events.slice(0, MAX_PER_MONTH_CELL);
            const hidden = events.length - shown.length;
            const inMonth = day.getMonth() === month;
            const isToday = iso === todayISO;
            return (
              <div
                key={iso}
                className={cn(
                  'flex min-h-[108px] flex-col gap-0.5 border-b border-r p-1 last:border-r-0',
                  !inMonth && 'bg-muted/40',
                  isToday && 'border-t-2 border-t-primary bg-primary/5'
                )}
              >
                <div
                  className={cn(
                    'px-1 text-xs font-medium',
                    inMonth ? 'text-foreground' : 'text-muted-foreground/60',
                    isToday && 'text-primary-pressed'
                  )}
                >
                  {day.getDate()}
                </div>
                {shown.map((item) => (
                  <MonthEventRow key={eventKey(item)} item={item} onClick={onEventClick} mode={mode} />
                ))}
                {hidden > 0 ? (
                  <button
                    type="button"
                    onClick={() => onMoreClick(iso)}
                    className="cursor-pointer px-1 text-left text-xs font-medium text-primary hover:underline"
                  >
                    mais +{hidden}
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ── Semana ──────────────────────────────────────────────────────────────────

function WeekView({
  days,
  byDay,
  todayISO,
  onEventClick,
  mode,
}: {
  days: Date[];
  byDay: Map<string, AgendaEventItem[]>;
  todayISO: string;
  onEventClick: (item: AgendaEventItem) => void;
  mode: AgendaRowMode;
}) {
  return (
    <div>
      <WeekdayHeaderRow />
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const iso = toISODay(day);
          const events = byDay.get(iso) ?? [];
          const isToday = iso === todayISO;
          return (
            <div
              key={iso}
              className={cn(
                'flex min-h-[420px] flex-col gap-0.5 border-r p-1 last:border-r-0',
                isToday && 'border-t-2 border-t-primary bg-primary/5'
              )}
            >
              <div
                className={cn(
                  'px-1 text-xs font-medium',
                  isToday ? 'text-primary-pressed' : 'text-foreground'
                )}
              >
                {day.getDate()}
              </div>
              {events.map((item) => (
                <MonthEventRow key={eventKey(item)} item={item} onClick={onEventClick} mode={mode} />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Dia ─────────────────────────────────────────────────────────────────────

function DayView({
  dayISO,
  byDay,
  onEventClick,
  mode,
}: {
  dayISO: string;
  byDay: Map<string, AgendaEventItem[]>;
  onEventClick: (item: AgendaEventItem) => void;
  mode: AgendaRowMode;
}) {
  const events = byDay.get(dayISO) ?? [];
  return (
    <div className="min-h-[320px] space-y-1 p-3">
      {events.length === 0 ? (
        <p className="px-1 py-2 text-sm text-muted-foreground">Sem eventos neste dia</p>
      ) : (
        events.map((item) => (
          <FullEventRow key={eventKey(item)} item={item} onClick={onEventClick} mode={mode} />
        ))
      )}
    </div>
  );
}

// ── Lista ───────────────────────────────────────────────────────────────────

function ListView({
  days,
  byDay,
  onEventClick,
  mode,
}: {
  days: Date[];
  byDay: Map<string, AgendaEventItem[]>;
  onEventClick: (item: AgendaEventItem) => void;
  mode: AgendaRowMode;
}) {
  const daysWithEvents = days.filter((d) => (byDay.get(toISODay(d)) ?? []).length > 0);
  if (daysWithEvents.length === 0) {
    return <EmptyState message="Não há eventos neste período" className="h-64" />;
  }
  return (
    <div className="divide-y divide-divider">
      {daysWithEvents.map((day) => {
        const iso = toISODay(day);
        const events = byDay.get(iso) ?? [];
        return (
          <div key={iso} className="px-3 py-2.5">
            <h3 className="mb-1.5 px-2 text-sm font-semibold">
              {format(day, "EEEE, d 'de' MMMM", { locale: ptBR })}
            </h3>
            <div className="space-y-1">
              {events.map((item) => (
                <FullEventRow key={eventKey(item)} item={item} onClick={onEventClick} mode={mode} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
